import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PdfGoruntuleyici.css';

/**
 * Çok sayfalı PDF görüntüleyici.
 *
 * NEDEN VAR: PDF'ler daha önce <iframe> içinde gösteriliyordu. iOS (Safari ve
 * uygulamanın WKWebView'i) iframe'e konan bir PDF'in yalnızca İLK SAYFASINI
 * çizer ve içeriği kaydırmaya izin vermez. Bu yüzden telefondan çok sayfalı
 * belgeler görüntülenemiyordu. Burada sayfalar pdf.js ile tek tek canvas'a
 * çizilip normal bir div içinde alt alta diziliyor; kaydırma artık sıradan
 * sayfa kaydırması olduğu için her cihazda çalışıyor.
 *
 * Sayfalar TEMBEL çiziliyor: yalnızca görünüm alanına yaklaşan sayfalar
 * işleniyor. 200 sayfalık bir belgenin tamamını birden çizmek telefonda
 * belleği tüketip sekmeyi düşürüyordu.
 */

let pdfjsSozu = null;

/** pdf.js yalnızca bir PDF açıldığında indirilir (ayrı chunk). */
const pdfjsYukle = async () => {
  if (!pdfjsSozu) {
    pdfjsSozu = import('pdfjs-dist/build/pdf.min.mjs').then((mod) => {
      const lib = mod.default || mod;
      lib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
      return lib;
    });
  }
  return pdfjsSozu;
};

/** Telefonlarda bellek taşmasın diye piksel oranı sınırlanır. */
const pikselOrani = () => Math.min(window.devicePixelRatio || 1, 2);

const Sayfa = ({ pdf, sayfaNo, genislik }) => {
  const kutuRef = useRef(null);
  const canvasRef = useRef(null);
  const [cizildi, setCizildi] = useState(false);
  const [oran, setOran] = useState(1.414); // A4 varsayılanı (yükseklik/genişlik)
  const cizimRef = useRef(null);

  // Sayfa boyutunu önceden al: yer tutucu doğru yükseklikte olsun,
  // böylece kaydırma çubuğu zıplamaz.
  useEffect(() => {
    let iptal = false;
    pdf.getPage(sayfaNo).then((page) => {
      if (iptal) return;
      const vp = page.getViewport({ scale: 1 });
      setOran(vp.height / vp.width);
    }).catch(() => { /* yoksay */ });
    return () => { iptal = true; };
  }, [pdf, sayfaNo]);

  const ciz = useCallback(async () => {
    if (cizildi || !canvasRef.current || !genislik) return;
    try {
      const page = await pdf.getPage(sayfaNo);
      const temelVp = page.getViewport({ scale: 1 });
      const olcek = (genislik / temelVp.width) * pikselOrani();
      const vp = page.getViewport({ scale: olcek });

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.floor(vp.width);
      canvas.height = Math.floor(vp.height);
      canvas.style.width = '100%';
      canvas.style.height = 'auto';

      const gorev = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
      cizimRef.current = gorev;
      await gorev.promise;
      setCizildi(true);
    } catch (_) {
      // Çizim iptal edilmiş olabilir (hızlı kaydırma); sessizce geç.
    }
  }, [pdf, sayfaNo, genislik, cizildi]);

  // Görünüm alanına yaklaşınca çiz.
  //
  // root olarak KAPSAYICI değil, görünüm alanı (null) kullanılıyor.
  // Kapsayıcı ref'i ilk render'da henüz null olduğu için gözlemci yanlış
  // köke bağlanabiliyordu. null kök, ata öğelerin kırpmasını da hesaba
  // katar; bu yüzden kaydırma kutusu içinde de doğru çalışır.
  useEffect(() => {
    const kutu = kutuRef.current;
    if (!kutu) return undefined;

    // Gözlemci yoksa (çok eski tarayıcı) sayfayı doğrudan çiz.
    if (typeof IntersectionObserver === 'undefined') { ciz(); return undefined; }

    const gozlemci = new IntersectionObserver(
      (girisler) => { if (girisler.some((g) => g.isIntersecting)) ciz(); },
      { rootMargin: '800px 0px' }
    );
    gozlemci.observe(kutu);
    return () => gozlemci.disconnect();
  }, [ciz]);

  useEffect(() => () => {
    try { cizimRef.current?.cancel(); } catch (_) { /* yoksay */ }
  }, []);

  return (
    <div
      ref={kutuRef}
      className="pdfg-sayfa"
      style={cizildi ? undefined : { paddingTop: `${oran * 100}%` }}
    >
      <canvas ref={canvasRef} className={cizildi ? 'pdfg-canvas' : 'pdfg-canvas pdfg-bos'} />
      {!cizildi && <span className="pdfg-sayfa-no">{sayfaNo}</span>}
    </div>
  );
};

const PdfGoruntuleyici = ({ kaynak, dosyaAdi }) => {
  const sarmalRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [sayfaSayisi, setSayfaSayisi] = useState(0);
  const [genislik, setGenislik] = useState(0);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);

  // Belgeyi aç
  useEffect(() => {
    let iptal = false;
    let belge = null;

    (async () => {
      setYukleniyor(true);
      setHata(null);
      try {
        const pdfjs = await pdfjsYukle();
        const gorev = pdfjs.getDocument(
          kaynak instanceof Blob
            ? { data: await kaynak.arrayBuffer() }
            : { url: kaynak }
        );
        belge = await gorev.promise;
        if (iptal) { belge.destroy(); return; }
        setPdf(belge);
        setSayfaSayisi(belge.numPages);
      } catch (err) {
        if (!iptal) setHata(err?.message || 'Belge açılamadı.');
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    })();

    return () => {
      iptal = true;
      try { belge?.destroy(); } catch (_) { /* yoksay */ }
    };
  }, [kaynak]);

  // Kapsayıcı genişliğini izle
  useEffect(() => {
    const el = sarmalRef.current;
    if (!el) return undefined;

    const olc = () => {
      const w = el.clientWidth - 24; // yatay boşluk payı
      if (w > 0) setGenislik(w);
    };
    olc();

    const gozlemci = new ResizeObserver(olc);
    gozlemci.observe(el);
    return () => gozlemci.disconnect();
  }, [pdf]);

  if (hata) {
    return (
      <div className="pdfg-durum pdfg-hata">
        <strong>Belge görüntülenemedi</strong>
        <span>{hata}</span>
      </div>
    );
  }

  return (
    <div className="pdfg-sarmal" ref={sarmalRef}>
      {yukleniyor && <div className="pdfg-durum">Belge açılıyor…</div>}

      {pdf && sayfaSayisi > 0 && (
        <>
          {sayfaSayisi > 1 && (
            <div className="pdfg-bilgi">
              {dosyaAdi ? `${dosyaAdi} · ` : ''}{sayfaSayisi} sayfa — aşağı kaydırarak tümünü görebilirsiniz
            </div>
          )}
          <div className="pdfg-sayfalar">
            {Array.from({ length: sayfaSayisi }, (_, i) => (
              <Sayfa
                key={i + 1}
                pdf={pdf}
                sayfaNo={i + 1}
                genislik={genislik}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PdfGoruntuleyici;
